import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, TenantResponse } from '../lib/api';
import GeneratePlaceIdModal from '../components/GeneratePlaceIdModal';
import GoogleDriveConnectButton from '../components/GoogleDriveConnectButton';
import { CodeResponse } from '@react-oauth/google';
import { isGoogleOAuthConfigured } from '../lib/google-oauth';

interface ExtraData {
    [key: string]: string | CodeResponse | boolean | null | unknown;
}

const CreateTask: React.FC = () => {

    const [frequency, setFrequency] = useState('');
    const [scheduleName, setScheduleName] = useState('');
    // const [placeId, setPlaceId] = useState('');
    const [extraData, setExtraData] = useState<ExtraData>({});
    const [tenantId, setTenantId] = useState("");
    const [tenants, setTenants] = useState<TenantResponse[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isAllTenant, setIsAllTenant] = useState(false);
    const navigate = useNavigate();

    const [openGeneratePlaceIdModal, setOpenGeneratePlaceIdModal] = useState(false);

    const handleOnPlaceIdGenerated = (placeId: string) => {
        setExtraData({ ...extraData, place_id: placeId });
        setOpenGeneratePlaceIdModal(false);
    };

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const fetchedTenants = await apiClient.getTenants();
                setTenants(fetchedTenants);
                if (fetchedTenants.length > 0) {
                    setTenantId(fetchedTenants[0].id); // Default to the first tenant
                }
            } catch (err) {
                console.error("Failed to fetch tenants", err);
                setError("Failed to load tenants. Please try again.");
            }
        };
        fetchTenants();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        if (!frequency || !scheduleName) {
            setError('Task Schedule, frequency are required.');
            setIsLoading(false);
            return;
        }

        if (!isAllTenant && !tenantId) {
            setError('Tenant is required.');
            setIsLoading(false);
            return;
        }

        if (isAllTenant) {
            setTenantId(tenants[0].id);
        }

        if (scheduleName === 'pull_google_review' && !extraData.place_id) {
            setError('Google Place ID is required.');
            setIsLoading(false);
            return;
        }


        if (scheduleName === 'pull_knowledge_base' && !extraData.driveCode) {
            setError('Google Drive connection is required.');
            setIsLoading(false);
            return;
        }

        try {
            await apiClient.createTask({
                tenant_id: tenantId,
                schedule_name: scheduleName,
                frequency,
                extra_data: {
                    ...extraData, isAllTenant
                }
            });
            navigate('/tasks');
        } catch (err: any) {
            console.log(err);
            const error = err.message || 'Failed to create task. Please try again.';
            setError(error);
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">Create New Task</h1>
            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{error}</div>}
            <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">

                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="role">
                        Task Schedule
                    </label>
                    <select
                        id="scheduleName"
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        value={scheduleName}
                        required
                        onChange={(e) => setScheduleName(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="">Select Task</option>
                        <option value="pull_google_review">Pull Google Review</option>
                        <option value="pull_knowledge_base">Pull Knowledge Base</option>
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="role">
                        Frequency
                    </label>
                    <select
                        id="role"
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        value={frequency}
                        required
                        onChange={(e) => setFrequency(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="">Select Frequency</option>
                        <option value="monthly">Monthly</option>
                        <option value="biweekly">Bi-Weekly</option>
                        <option value="weekly">Weekly</option>
                        <option value="daily">Daily</option>
                    </select>
                </div>

                {scheduleName && scheduleName === 'pull_knowledge_base' && (<div className="mb-4 items-center gap-2">
                    <input
                        id="isAllTenant"
                        type="checkbox"
                        // className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        checked={isAllTenant}
                        onChange={(e) => setIsAllTenant(e.target.checked)}
                        disabled={isLoading}
                    /> <label className=" text-gray-700 ml-2 text-sm font-bold mb-2" htmlFor="isAllTenant">
                        All Tenant
                    </label>
                </div>)}

                {!isAllTenant && (<div className="mb-6">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="tenantId">
                        Tenant
                    </label>
                    <select
                        id="tenantId"
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700  leading-tight focus:outline-none focus:shadow-outline"
                        value={tenantId}
                        onChange={(e) => setTenantId(e.target.value)}
                        disabled={isLoading}
                    >

                        {tenants.length === 0 && <option>Loading tenants...</option>}
                        <option value="">Select Tenant</option>
                        {tenants.map(tenant => (
                            <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                        ))}
                    </select>
                </div>)}

                {scheduleName && (
                    <>
                        {scheduleName === 'pull_google_review' && (
                            <>
                                <div className="mb-6">
                                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="tenantId">
                                        Google Place ID
                                    </label>
                                    <div className='flex gap-4'> <input
                                        className="flex-1 shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        id="name"
                                        type="text"
                                        required={true}
                                        placeholder="Google Place ID (Required)"
                                        value={extraData['place_id']?.toString()}
                                        onChange={(e) => setExtraData({ ...extraData, place_id: e.target.value })}
                                    />
                                        <button
                                            className="bg-zinc-600 hover:bg-zinc-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                                            type="button"
                                            onClick={() => {
                                                setOpenGeneratePlaceIdModal(true);
                                            }}
                                        >
                                            Generate Place ID
                                        </button>
                                    </div>
                                </div>
                            </>)


                        }
                        <>
                            {scheduleName === 'pull_knowledge_base' && (
                                <div className="mb-6">
                                    {isGoogleOAuthConfigured ? (
                                        <GoogleDriveConnectButton
                                            driveCode={extraData.driveCode as CodeResponse | undefined}
                                            disabled={isLoading}
                                            onConnected={(driveCode, oauthToken) =>
                                                setExtraData({ ...extraData, driveCode, oauthToken })
                                            }
                                        />
                                    ) : (
                                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                            Set <code className="text-xs">VITE_GOOGLE_CLIENT_ID</code> in{' '}
                                            <code className="text-xs">lawma-admin-ui/.env</code> to connect Google Drive.
                                        </p>
                                    )}
                                </div>)}
                        </>

                    </>
                )}



                <div className="flex items-center justify-between">
                    <button
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Creating...' : 'Create Task'}
                    </button>
                    <button
                        className="inline-block align-baseline font-bold text-sm text-blue-500 hover:text-blue-800"
                        type="button"
                        onClick={() => navigate('/tasks')}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                </div>
            </form>

            {openGeneratePlaceIdModal && (
                <GeneratePlaceIdModal
                    isOpen={openGeneratePlaceIdModal}
                    onClose={() => setOpenGeneratePlaceIdModal(false)}
                    onPlaceIdGenerated={handleOnPlaceIdGenerated}
                />
            )}
        </div>
    );
};

export default CreateTask;
